import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PRODUCT_URL } from '@insite-store360/shared';
import { catchError, map, Observable } from 'rxjs';
import {
  Product,
  ProductLine,
  ScheduleGrossPrice,
  Status,
  Subcategory,
} from '../shared/src/lib/shared';
import { ErrorHandlerService } from './error-handler.service';

export interface ProjectNameFilterOption {
  id: number;
  name: string;
  allIds: number[];
}

@Injectable({
  providedIn: 'root',
})
export class ProductserviceService {
  // private readonly PRODUCT_SEVICE_URL = `${environment.apiUrl}`;

  private readonly PRODUCT_SEVICE_URL = `${PRODUCT_URL}`;
  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }



  public getProjectTypeByCountryId(params: {
    countryId: number;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryId', params.countryId);
    return this.http.get<any>(`${this.PRODUCT_SEVICE_URL}/product/get-project-type-by-country-id`, {
      params: queryParams,
    })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //// product services
  public registerProduct(formData: FormData): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/product/save`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public productGetAll(params: {
    pageIndex: any;
    pageSize: any;
    searchFilter?: string;
    countryIds?: number[];
    categoryId?: number[];
    subCategoryId?: number[];
    brandId?: number[];
    userRoleId?: number[];
    status?: number;
    source?: string;
    projectNameId?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.userRoleId && params.userRoleId.length > 0) {
      queryParams = queryParams.set('userRoleId', params.userRoleId.join(','));
    }
    if (params.categoryId && params.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }
    if (params.subCategoryId && params.subCategoryId.length > 0) {
      queryParams = queryParams.set(
        'subCategoryId',
        params.subCategoryId.join(',')
      );
    }
    if (params.brandId && params.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }

    if (params.status !== undefined) {
      queryParams = queryParams.set('status', params.status);
    }

    if (params.source) {
      queryParams = queryParams.set('source', params.source);
    }

    const projectNameIds = Array.isArray(params.projectNameId)
      ? params.projectNameId
      : params.projectNameId != null && params.projectNameId !== ''
        ? [params.projectNameId]
        : [];
    if (projectNameIds.length > 0) {
      queryParams = queryParams.set(
        'projectNameId',
        projectNameIds.join(','),
      );
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/getAll`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getProductByCountryId(countryId: number): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryId', countryId);
    return this.http.get<any>(`${this.PRODUCT_SEVICE_URL}/product/get-product-by-country-id`, {
      params: queryParams,
    })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Trade Calendar wizard — searchable products by country + subcategory. */
  public getProductsForTradeCalendar(
    countryId: number,
    options?: { search?: string; subcategoryIds?: number[]; limit?: number },
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryId', countryId)
      .set('limit', String(options?.limit ?? 50));
    if (options?.search?.trim()) {
      queryParams = queryParams.set('search', options.search.trim());
    }
    if (options?.subcategoryIds?.length) {
      queryParams = queryParams.set('subcategoryIds', options.subcategoryIds.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/get-products-for-trade-calendar`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDashboardProduct(params: {
    pageIndex: any;
    pageSize: any;
    searchFilter?: string;
    countryId?: number[];
    categoryId?: number[];
    subCategoryId?: number[];
    series?: string[];
    status?: number;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }

    if (params.categoryId && params.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }

    if (params.subCategoryId && params.subCategoryId.length > 0) {
      queryParams = queryParams.set(
        'subCategoryId',
        params.subCategoryId.join(',')
      );
    }

    if (params.series && params.series.length > 0) {
      queryParams = queryParams.set('series', params.series.join(','));
    }

    if (params.status !== undefined) {
      queryParams = queryParams.set('status', params.status);
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/getDashboardProduct`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }
  public getCountryBasedOnRole(params: {
    countryIds?: number[];
    userRoleId?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.userRoleId && params.userRoleId.length > 0) {
      queryParams = queryParams.set('userRoleId', params.userRoleId.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/getCountryBasedOnRole`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateProduct(formData: FormData): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/product/update`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateFileProduct(formData: FormData): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/product/updateFile`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteProduct(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/product/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  public blockAdmin(adminStatus: Status): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/product/changeStatus/${adminStatus.id}`,
        adminStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /////    Product Line services
  public createProductLine(admin: Product): Observable<Product> {
    return this.http.post<Product>(
      `${this.PRODUCT_SEVICE_URL}/productLine/save`,
      admin
    );
  }

  public productLineGetAll(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    status?: number
  ): Observable<any> {
    if (searchFilter) {
      return this.http
        .get<any>(
          `${this.PRODUCT_SEVICE_URL}/productLine/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}&searchFilter=${searchFilter}&status=${status}`
        ) // Include the data payload here
        .pipe(catchError(this.errorHandler.handleError));
    } else {
      return this.http
        .get<any>(
          `${this.PRODUCT_SEVICE_URL}/productLine/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}&status=${status}`
        ) // Include the data payload here
        .pipe(catchError(this.errorHandler.handleError));
    }
  }
  public updateProductLine(data: Product): Observable<Product> {
    return this.http
      .post<Product>(`${this.PRODUCT_SEVICE_URL}/productLine/update`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteProductLine(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/productLine/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /// category

  public createCategory(admin: Product): Observable<Product> {
    return this.http.post<Product>(
      `${this.PRODUCT_SEVICE_URL}/category/save`,
      admin
    );
  }
  public getAllCategory(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    status?: number
  ): Observable<any> {
    if (searchFilter) {
      return this.http
        .get<any>(
          `${this.PRODUCT_SEVICE_URL}/category/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}&searchFilter=${searchFilter}&status=${status}`
        ) // Include the data payload here
        .pipe(catchError(this.errorHandler.handleError));
    } else {
      return this.http
        .get<any>(
          `${this.PRODUCT_SEVICE_URL}/category/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}&status=${status}`
        ) // Include the data payload here
        .pipe(catchError(this.errorHandler.handleError));
    }
  }
  public getByProductLineId(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/category/getByProductLineId/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateCategory(data: Product): Observable<Product> {
    return this.http
      .post<Product>(`${this.PRODUCT_SEVICE_URL}/category/update`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteCategory(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/category/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // sub category services

  public createSubCategory(admin: Product): Observable<Product> {
    return this.http.post<Product>(
      `${this.PRODUCT_SEVICE_URL}/subCategory/save`,
      admin
    );
  }
  public getAllSubCategory(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    status: number = 2
  ): Observable<any> {
    if (searchFilter) {
      return this.http
        .get<any>(
          `${this.PRODUCT_SEVICE_URL}/subCategory/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}&searchFilter=${searchFilter}&status=${status}`
        ) // Include the data payload here
        .pipe(catchError(this.errorHandler.handleError));
    } else {
      return this.http
        .get<any>(
          `${this.PRODUCT_SEVICE_URL}/subCategory/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}&status=${status}`
        ) // Include the data payload here
        .pipe(catchError(this.errorHandler.handleError));
    }
  }
  public getByCategoryId(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/subCategory/getByCategoryId/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getByCategoryIdWithCount(
    categoryId: number,
    month: number,
    year: number,
    countryId: number[]
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (month) {
      queryParams = queryParams.set('month', month);
    }
    if (year) {
      queryParams = queryParams.set('year', year);
    }
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (categoryId) {
      queryParams = queryParams.set('categoryId', categoryId);
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/subCategory/getByCategoryIdWithCount`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateSubCategory(data: Product): Observable<Product> {
    return this.http
      .post<Product>(`${this.PRODUCT_SEVICE_URL}/subCategory/update`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteSubCategory(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/subCategory/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public toggleStatusCategory(adminStatus: Status): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/category/changeStatus/${adminStatus.id}`,
        adminStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllCategoryStatus(searchFilter?: string): Observable<any> {
    let queryParams = new HttpParams();
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/category/getAllActive`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }
  public toggleStatusSubCategory(adminStatus: Status): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/subCategory/changeStatus/${adminStatus.id}`,
        adminStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllSubCategoryStatus(searchFilter?: string): Observable<any> {
    let queryParams = new HttpParams();
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/subCategory/getAllActive`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public toggleStatusProductLine(adminStatus: Status): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/productLine/changeStatus/${adminStatus.id}`,
        adminStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllProductlineStatus(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/productLine/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public toggleStatusProduct(adminStatus: Status): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/product/changeStatus/${adminStatus.id}`,
        adminStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public toggleStarProduct(id: number, starProduct: boolean): Observable<any> {
    return this.http
      .put<any>(`${this.PRODUCT_SEVICE_URL}/product/changeStarProduct/${id}`, {
        starProduct,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public togglePricePromotion(id: number, isPricePromotion: boolean): Observable<any> {
    return this.http
      .put<any>(`${this.PRODUCT_SEVICE_URL}/product/changePricePromotion/${id}`, {
        isPricePromotion,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllProductStatus(searchFilter?: string): Observable<any> {
    let queryParams = new HttpParams();
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/getAllActive`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Imports a list of regions into the server.
   * @param data - An array of regions objects to be imported.
   * @returns An Observable that emits an array of imported regions objects upon successful import.
   */
  public importProductLine(
    data: Record<string, any>
  ): Observable<ProductLine[]> {
    return this.http
      .post<ProductLine[]>(
        `${this.PRODUCT_SEVICE_URL}/productLine/importData`,
        data
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public excelDownloadProductLine() {
    return this.http
      .get(`${this.PRODUCT_SEVICE_URL}/productLine/downloadExcelTemplate`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Imports a list of regions into the server.
   * @param data - An array of regions objects to be imported.
   * @returns An Observable that emits an array of imported regions objects upon successful import.
   */
  public importCategory(data: Record<string, any>): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.PRODUCT_SEVICE_URL}/category/importData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public excelDownloadCategory() {
    return this.http
      .get(`${this.PRODUCT_SEVICE_URL}/category/downloadExcelTemplate`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Imports a list of regions into the server.
   * @param data - An array of regions objects to be imported.
   * @returns An Observable that emits an array of imported regions objects upon successful import.
   */
  public importSubcategory(
    data: Record<string, any>
  ): Observable<Subcategory[]> {
    return this.http
      .post<Subcategory[]>(
        `${this.PRODUCT_SEVICE_URL}/subCategory/importData`,
        data
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public excelDownloadSubcategory() {
    return this.http
      .get(`${this.PRODUCT_SEVICE_URL}/subCategory/downloadExcelTemplate`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }
  // public importProduct(data: Record<string, any>): Observable<any[]> {
  //   return this.http
  //     .post<any[]>(`${this.PRODUCT_SEVICE_URL}/product/importData`, data)
  //     .pipe(catchError(this.errorHandler.handleError));
  // }

  // public downloadExcelTemplate(countryId?: number) {
  //   let params: any = { responseType: 'blob' as 'json' };
  //   if (countryId) {
  //     params.params = { countryId };
  //   }
  //   return this.http
  //     .get(`${this.PRODUCT_SEVICE_URL}/product/downloadExcelTemplate`, params)
  //     .pipe(catchError(this.errorHandler.handleError));
  // }
  /// export template service
  public exportProductLine(): Observable<Blob> {
    const columns: string[] = ['Product Line', 'Status', 'Created By', 'Created Date & Time', 'Updated By', 'Updated Date & Time']; //export needed columns
    // const columns:string[] = []; //export all columns
    return this.http
      .post<Blob>(
        `${this.PRODUCT_SEVICE_URL}/productLine/downloadExcelReport`,
        { columns },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }
  public exportCategory(): Observable<Blob> {
    const columns: string[] = ['ProductLine', 'Category', 'Status', 'Created By', 'Created Date & Time', 'Updated By', 'Updated Date & Time']; //export needed columns
    // const columns:string[] = []; //export all columns
    return this.http
      .post<Blob>(
        `${this.PRODUCT_SEVICE_URL}/category/downloadExcelReport`,
        { columns },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }
  public exportSubCategory(): Observable<Blob> {
    const columns: string[] = ['Category', 'Sub Category', 'Brand', 'Status', 'Created By', 'Created Date & Time', 'Updated By', 'Updated Date & Time']; //export needed columns
    // const columns:string[] = []; //export all columns
    return this.http
      .post<Blob>(
        `${this.PRODUCT_SEVICE_URL}/subCategory/downloadExcelReport`,
        { columns },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  // public exportProduct(params?: {
  //   searchFilter?: string,
  //   countryId?: number[],
  //   categoryId?: number[],
  //   subCategoryId?: number[],
  //   series?: string[],
  // }): Observable<Blob> {
  //   let queryParams = new HttpParams()
  //   if (params?.searchFilter) {
  //     queryParams = queryParams.set('searchFilter', params.searchFilter);
  //   }

  //   if (params?.countryId && params?.countryId.length > 0) {
  //     queryParams = queryParams.set('countryId', params.countryId.join(','));
  //   }

  //   if (params?.categoryId && params?.categoryId.length > 0) {
  //     queryParams = queryParams.set('categoryId', params.categoryId.join(','));
  //   }

  //   if (params?.subCategoryId && params?.subCategoryId.length > 0) {
  //     queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
  //   }

  //   if (params?.series && params?.series.length > 0) {
  //     queryParams = queryParams.set('series', params.series.join(','));
  //   }

  //   const columns: string[] = [
  //     'productLine',
  //     'Category',
  //     'SubCategory',
  //     'Name',
  //     'PNC Code',
  //     'Segement',
  //     'Model',
  //     'Series',
  //     'Project Name',
  //   ]; //export needed columns
  //   // const columns:string[] = []; //export all columns
  //   return this.http
  //     .post<Blob>(
  //       `${this.PRODUCT_SEVICE_URL}/product/downloadExcelReport`,
  //       { columns },
  //       {
  //         params: queryParams,
  //         responseType: 'blob' as 'json',
  //       }
  //     )
  //     .pipe(catchError(this.errorHandler.handleError));
  // }

  //// brand services
  public registerBrand(admin: Product): Observable<Product> {
    return this.http.post<Product>(
      `${this.PRODUCT_SEVICE_URL}/brand/save`,
      admin
    );
  }

  public registerBrandWithImage(formData: FormData): Observable<Product> {
    return this.http
      .post<Product>(`${this.PRODUCT_SEVICE_URL}/brand/save`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateBrandWithImage(formData: FormData): Observable<Product> {
    return this.http
      .put<Product>(`${this.PRODUCT_SEVICE_URL}/brand/update`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public brandGetAll(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    status: number = 2,
    countryIds?: number[]
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (status) {
      queryParams = queryParams.set('status', status);
    }

    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/brand/getAll`, {
        params: queryParams,
      }) // Include the data payload here
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getBrandDetailsById(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/brand/brandDetails/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To fetch brand based on the country id
  public getBrandByCountryId(countryId: number): Observable<any> {
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/getBrandBycountryId/${countryId}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateBrand(data: Product): Observable<Product> {
    return this.http
      .put<Product>(`${this.PRODUCT_SEVICE_URL}/brand/update`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteBrand(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/brand/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  public getAllBrandStatus(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/brand/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  public toggleStatusBrand(adminStatus: Status): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/changeStatus/${adminStatus.id}`,
        adminStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }
  public exportBrand(): Observable<Blob> {
    const columns: string[] = ['Brand', 'Competitor', 'Status', 'Country', 'Created By', 'Created Date & Time', 'Updated By', 'Updated Date & Time']; //export needed columns
    // const columns:string[] = []; //export all columns
    return this.http
      .post<Blob>(
        `${this.PRODUCT_SEVICE_URL}/brand/downloadExcelReport`,
        { columns },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }
  public excelDownloadBrand() {
    return this.http
      .get(`${this.PRODUCT_SEVICE_URL}/brand/downloadExcelTemplate`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importBrand(data: Record<string, any>): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.PRODUCT_SEVICE_URL}/brand/importData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getByBrandId(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/brand/getByBrandId/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Getting brand details based on the category id.
   * @param id - Passing the Category Id.
   * @returns An Observable that return the Brand details.
   */
  public getBrandBySubCategoryId(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/brand/getBySubCategoriesId/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //// segment services
  public getAllUomStatus(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product-segment/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createSegment(admin: Product): Observable<Product> {
    return this.http.post<Product>(
      `${this.PRODUCT_SEVICE_URL}/product-segment/save`,
      admin
    );
  }
  public getAllSegment(
    pageIndex?: number,
    pageSize?: number,
    searchFilter?: string,
    status?: number
  ): Observable<any> {
    if (searchFilter) {
      return this.http
        .get<any>(
          `${this.PRODUCT_SEVICE_URL}/product-segment/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}&searchFilter=${searchFilter}&status=${status}`
        ) // Include the data payload here
        .pipe(catchError(this.errorHandler.handleError));
    } else if (!status && !pageIndex && !pageSize && !searchFilter) {
      return this.http
        .get<any>(`${this.PRODUCT_SEVICE_URL}/product-segment/getAll`)
        .pipe(catchError(this.errorHandler.handleError));
    } else {
      return this.http
        .get<any>(
          `${this.PRODUCT_SEVICE_URL}/product-segment/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}&status=${status}`
        ) // Include the data payload here
        .pipe(catchError(this.errorHandler.handleError));
    }
  }
  public updateSegment(data: Product): Observable<Product> {
    return this.http
      .post<Product>(`${this.PRODUCT_SEVICE_URL}/product-segment/update`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteSegment(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/product-segment/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  public toggleStatusSegment(adminStatus: Status): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/product-segment/changeStatus/${adminStatus.id}`,
        adminStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createScheduleGrossPrice(
    dateRange: ScheduleGrossPrice
  ): Observable<ScheduleGrossPrice> {
    return this.http.post<ScheduleGrossPrice>(
      `${this.PRODUCT_SEVICE_URL}/productPricingDateRange/create`,
      dateRange
    );
  }
  public getAllScheduleGrossPrice(countryId?: number): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/productPricingDateRange/getAll`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteSchedulePrice(id: number): Observable<any> {
    return this.http
      .delete<any>(
        `${this.PRODUCT_SEVICE_URL}/productPricingDateRange/delete/${id}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }
  public exportProductPriceRange(
    priceRangeId?: any,
    fromDate?: any,
    toDate?: any,
    countryId?: number
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (priceRangeId !== undefined && priceRangeId !== null) {
      queryParams = queryParams.set('priceRangeId', priceRangeId.toString());
    }
    if (fromDate) {
      queryParams = queryParams.set('fromDate', fromDate.toString());
    }
    if (toDate) {
      queryParams = queryParams.set('toDate', toDate.toString());
    }
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }
    return this.http
      .get<Blob>(
        `${this.PRODUCT_SEVICE_URL}/productPrice/downloadExcelTemplate`,
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public checkExistingDateRange(
    fromDate: string,
    toDate: string,
    countryId?: number
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/productPricingDateRange/checkExistingDateRange?fromDate=${fromDate}&toDate=${toDate}`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importProductPriceTemplate(
    productPrice: any,
    priceDateRangeId: number | null,
    fromDate?: string,
    toDate?: string,
    countryId?: number
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (priceDateRangeId !== undefined && priceDateRangeId !== null) {
      queryParams = queryParams.set(
        'priceDateRangeId',
        priceDateRangeId.toString()
      );
    }
    if (fromDate) {
      queryParams = queryParams.set('fromDate', fromDate);
    }
    if (toDate) {
      queryParams = queryParams.set('toDate', toDate);
    }
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }
    return this.http
      .post<any>(
        `${this.PRODUCT_SEVICE_URL}/productPrice/importDataTemplate`,
        productPrice,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public applySellOutBackfill(
    priceDateRangeId: number,
    countryId?: number,
    updates?: Array<{ selloutId: number; newPrice: number; productId?: number }>,
  ): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/productPrice/applySellOutBackfill`, {
        priceDateRangeId,
        countryId,
        updates: updates ?? [],
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public previewSellOutBackfill(
    priceDateRangeId: number,
    countryId?: number
  ): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/productPrice/previewSellOutBackfill`, {
        priceDateRangeId,
        countryId,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllProductGrossPrice(params: {
    pageIndex: any;
    pageSize: any;
    id: number;
    searchFilter?: string;
    startDate?: string;
    endDate?: string;
    country?: number;
    account?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.country) {
      queryParams = queryParams.set('countryId', params.country);
    }
    if (params.account) {
      queryParams = queryParams.set('account', params.account);
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/productPrice/productPricingwithDateRange/${params.id}`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportProductGrossPrice(params: {
    pageIndex: any;
    pageSize: any;
    searchFilter?: string;
    startDate?: string;
    endDate?: string;
    country?: string;
    account?: string;
  }): Observable<Blob> {
    const columns: string[] = [
      'PNC',
      'Model',
      'Brand',
      'Account',
      'Country',
      'Category',
      'Sub Category',
      'Product',
      'Gross Price',
      'Price',
      'MDA/SDA',
      'FS/BI',
      'Project',
      'Series',
      'Start Date',
      'End Date',
    ];

    //export needed columns
    // const columns:string[] = []; //export all columns
    return this.http
      .post<Blob>(
        `${this.PRODUCT_SEVICE_URL}/productPrice/downloadExcelReport`,
        { columns, ...params },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get brand filter value by country id
  public getBrandFilterByCountry(
    countryId: any,
    searchFilter?: string
  ): Observable<any> {
    let queryParams = new HttpParams();
    // .set('countryId', countryId.toString());

    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('ids', countryId.join(','));
    }

    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/brand/getBrandFilterByCountry`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get brand filter value by country id
  public getMultiBrandFilterByCountry(
    countryId: any,
    searchFilter?: string
  ): Observable<any> {
    let queryParams = new HttpParams();
    // .set('countryId', countryId.toString());

    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('ids', countryId.join(','));
    }

    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/getMultiBrandFilterByCountry`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllActiveBrandsByCountry(
    countryId: any,
    searchFilter?: string
  ): Observable<any> {
    let queryParams = new HttpParams();

    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('ids', countryId.join(','));
    }

    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }

    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/getAllActiveBrandsByCountry`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get brand filter value by country id where no competitor brands
  public getBrandFilterByCountryNoCompetitor(
    countryId: any,
    searchFilter?: string
  ): Observable<any> {
    let queryParams = new HttpParams();
    // .set('countryId', countryId.toString());

    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('ids', countryId.join(','));
    }

    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/getBrandFilterByCountryNoCompetitor`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCategoryByCountry(params: {
    countryId: number[] | number | string;
    projectTypeId?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (Array.isArray(params.countryId)) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    } else if (typeof params.countryId === 'string') {
      queryParams = queryParams.set('countryId', params.countryId);
    } else if (typeof params.countryId === 'number') {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }

    if (params.projectTypeId) {
      queryParams = queryParams.set('projectTypeId', params.projectTypeId.join(','));
    }

    // Cache-bust so browser 304 does not return an empty body to Angular
    queryParams = queryParams.set('_t', Date.now().toString());

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/category/getCategoryByCountry`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getbyCategoryAndSubCategoryByProjectTypeId(params: {
    countryId: number[] | number | string;
    projectTypeId?: number | number[];
    categoryId?: number | number[];
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (Array.isArray(params.countryId)) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    } else if (typeof params.countryId === 'string') {
      queryParams = queryParams.set('countryId', params.countryId);
    } else if (typeof params.countryId === 'number') {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }

    if (params.projectTypeId) {
      if (Array.isArray(params.projectTypeId)) {
        queryParams = queryParams.set('projectTypeId', params.projectTypeId.join(','));
      } else {
        queryParams = queryParams.set('projectTypeId', params.projectTypeId);
      }
    }

    if (params.categoryId) {
      if (Array.isArray(params.categoryId)) {
        queryParams = queryParams.set('categoryId', params.categoryId.join(','));
      } else {
        queryParams = queryParams.set('categoryId', params.categoryId);
      }
    }

    // if (params.subCategoryId) {
    //   queryParams = queryParams.set('subCategoryId', params.subCategoryId);
    // }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/category/getbyCategoryAndSubCategoryByProjectTypeId`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllCategoryWithoutParams(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/category/getAllCategory`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllCountOfSubCategoryUnderCategory(params: {
    countryId: any[];
    category: number;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.category) {
      queryParams = queryParams.set('category', params.category);
    }

    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/category/getAllCountOfSubCategoryUnderCategory`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCategoryCount(params: {
    // searchFilter?: string,
    countryId?: number;
    // categoryId?: number,
    // subCategoryId?: number,
    // series?: string,
  }): Observable<any> {
    let queryParams = new HttpParams();
    // if (params.searchFilter) {
    //   queryParams = queryParams.set('searchFilter', params.searchFilter);
    // }

    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }

    // if (params.categoryId) {
    //   queryParams = queryParams.set('categoryId', params.categoryId);
    // }

    // if (params.subCategoryId) {
    //   queryParams = queryParams.set('subCategoryId', params.subCategoryId);
    // }

    // if (params.series) {
    //   queryParams = queryParams.set('series?.series', params?.series);
    // }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/getCategoryCount`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSubCategoryByCountry(params: {
    countryId: number;
    categoryId: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryId', params.countryId);

    if (Array.isArray(params.categoryId)) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/subCategory/getSubCategoryByCountry`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getProductSegment(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product-segment/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSegmentBySubCategoryId(subCategoryId: number): Observable<any> {
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/product-segment/getBySubCatId/${subCategoryId}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSeries(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/getSeries`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllDisplayType(
    term?: string,
    countryId?: number[],
    month?: string,
    accountId?: number[],
    storeId?: number[],
    brandId?: number[]
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (month) {
      queryParams = queryParams.set('month', month);
    }
    if (term) {
      queryParams = queryParams.set('searchFilter', term);
    }
    if (accountId && accountId.length > 0) {
      queryParams = queryParams.set('accountId', accountId.join(','));
    }
    if (storeId && storeId.length > 0) {
      queryParams = queryParams.set('storeId', storeId.join(','));
    }
    if (brandId && brandId.length > 0) {
      queryParams = queryParams.set('brandId', brandId.join(','));
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/displayType/displayTypes`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getByCategoryIds(categoryIds: number[]): Observable<any> {

    let queryParams = new HttpParams();
    if (categoryIds && categoryIds.length > 0) {
      queryParams = queryParams.set('categoryIds', categoryIds.join(','));
    }
    // Cache-bust so browser 304 does not return an empty body to Angular
    queryParams = queryParams.set('_t', Date.now().toString());
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/subCategory/getSubCategorysByCategoryIds`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCategoryAndSubCategoryByProductId(productId: number): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/getCategoryAndSubCategoryByProductId/${productId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCountryBrandMapping(
    pageIndex: number,
    pageSize: number,
    searchValue?: string,
    countryId?: number
  ): Observable<{
    result: Array<{
      brand: { id: number; name: string };
      country: { id: number; name: string };
    }>;
    paginatorValue: { totalCount: number; from: number; to: number };
  }> {
    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (searchValue) {
      queryParams = queryParams.set('searchValue', searchValue);
    }

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }

    return this.http
      .get<{
        result: Array<{
          brand: { id: number; name: string };
          country: { id: number; name: string };
        }>;
        paginatorValue: { totalCount: number; from: number; to: number };
      }>(`${this.PRODUCT_SEVICE_URL}/brand/getCountryBrandMapping`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createBrandCountryMapping(data: {
    countryId: number;
    brandIds: number[];
  }): Observable<any> {
    return this.http
      .post<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/createBrandCountryMapping`,
        data
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteBrandCountryMapping(countryId: number): Observable<any> {
    return this.http
      .delete<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/deleteBrandCountryMapping/${countryId}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateBrandCountryMapping(
    countryId: number,
    brandIds: number[]
  ): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/updateBrandCountryMapping/${countryId}`,
        { brandIds }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCategoryBrandMapping(
    pageIndex: number,
    pageSize: number,
    searchValue?: string,
    countryId?: number
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (searchValue) {
      queryParams = queryParams.set('searchValue', searchValue);
    }

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/brand/getCategoryBrandMapping`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createCategoryBrandMapping(data: {
    countryId: number;
    categoryId: number;
    brandIds: number[];
  }): Observable<any> {
    return this.http
      .post<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/createCategoryBrandMapping`,
        data
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateCategoryBrandMapping(data: {
    countryId: number;
    categoryId: number;
    brandIds: number[];
  }): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/updateCategoryBrandMapping`,
        data
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteCategoryBrandMapping(
    countryId: number,
    categoryId: number
  ): Observable<any> {
    return this.http
      .delete<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/deleteCategoryBrandMapping/${countryId}/${categoryId}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getModels(params?: {
    subCategoryId?: number;
    categoryId?: number;
    projectTypeId?: number;
    countryId?: number;
    searchFilter?: string;
  }): Observable<any[]> {
    let queryParams = new HttpParams();
    if (params?.subCategoryId) {
      queryParams = queryParams.set('subCategoryId', params.subCategoryId);
    }
    if (params?.categoryId) {
      queryParams = queryParams.set('categoryId', params.categoryId);
    }
    if (params?.projectTypeId) {
      queryParams = queryParams.set('projectTypeId', params.projectTypeId);
    }
    if (params?.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }
    if (params?.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<any[]>(`${this.PRODUCT_SEVICE_URL}/product/getModels`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //Get core range by account id and single country id
  public getCoreRangeByAccountAndCountry(
    accountId: number[],
    countryId: number
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (accountId && accountId.length > 0) {
      queryParams = queryParams.set('accountIds', accountId.join(','));
    }
    if (countryId && countryId !== null && countryId !== undefined) {
      queryParams = queryParams.set('countryIds', String(countryId));
    }
    // Cache-bust so browser 304 does not return an empty body to Angular
    queryParams = queryParams.set('_t', Date.now().toString());
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/coreRanges/getByCountryAndAccount`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //Get core range by account id and multiple country ids (wrapper that reuses the same endpoint)
  public getCoreRangeByAccountAndCountries(
    accountId: number[],
    countryIds: number[]
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (accountId && accountId.length > 0) {
      queryParams = queryParams.set('accountIds', accountId.join(','));
    }
    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    // Cache-bust so browser 304 does not return an empty body to Angular
    queryParams = queryParams.set('_t', Date.now().toString());
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/coreRanges/getByCountryAndAccount`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllPriceMappingTemplate(params: {
    pageIndex: number;
    pageSize: number;
    accountIds?: number[];
    countryIds?: number[];
    searchFilter?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.searchFilter && params.searchFilter.trim().length >= 3) {
      queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
    }

    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/product/getAllPriceMappingTemplate`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportPriceMappingTemplate(
    accountIds?: number[],
    countryIds?: number[],
    searchFilter?: string
  ): Observable<Blob> {
    let queryParams = new HttpParams();
    if (accountIds && accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', accountIds.join(','));
    }
    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    if (searchFilter && searchFilter.trim().length >= 3) {
      queryParams = queryParams.set('searchFilter', searchFilter.trim());
    }

    return this.http
      .post<Blob>(
        `${this.PRODUCT_SEVICE_URL}/product/priceMapping/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get un mapped product list
  public getUnMappedProduct(params: {
    countryIds?: number[];
    accountId?: number[];
    categoryId?: number[];
    subCategoryId?: number[];
    searchFilter?: string;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params?.countryIds && params.countryIds?.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params?.accountId && params.accountId?.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }
    if (params?.categoryId && params.categoryId?.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }
    if (params?.subCategoryId && params.subCategoryId?.length > 0) {
      queryParams = queryParams.set(
        'subCategoryId',
        params.subCategoryId.join(',')
      );
    }
    if (params?.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/productPrice/getUnMappedProduct`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get mapped product list
  public getMappedProduct(params: {
    countryIds?: number[];
    accountId?: number[];
    categoryId?: number[];
    subCategoryId?: number[];
    searchFilter?: string;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params?.countryIds && params.countryIds?.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params?.accountId && params.accountId?.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }
    if (params?.categoryId && params.categoryId?.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }
    if (params?.subCategoryId && params.subCategoryId?.length > 0) {
      queryParams = queryParams.set(
        'subCategoryId',
        params.subCategoryId.join(',')
      );
    }
    if (params?.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/productPrice/getMappedProduct`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To save the product line data
  public saveProductLine(bodyData: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/saveMap`, bodyData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To remove the product line data
  public removeProductLine(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/deleteMap/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getReasonForIsDisplayedPriceTricking(): Observable<any> {
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/getReasonForIsDisplayedPriceTricking`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  private buildPriceTrackingDashboardQueryParams(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
    coreRangeIds?: number[];
  }): HttpParams {
    let queryParams = new HttpParams();
    if ((filters?.countryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('countryIds', filters.countryIds.join(','));
    }
    if (filters?.year != null && !Number.isNaN(Number(filters.year))) {
      queryParams = queryParams.set('year', String(filters.year));
    }
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.accountIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'accountIds',
        filters.accountIds?.join(',') ?? ''
      );
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.productTierIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productTierIds',
        filters.productTierIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return queryParams;
  }

  public getPriceTrackingSyncStatus(): Observable<{
    lastSyncTime: string | null;
    syncedBy: number | null;
  }> {
    return this.http
      .get<{ result?: { lastSyncTime: string | null; syncedBy: number | null } }>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/syncStatus`
      )
      .pipe(
        map((r) => r?.result ?? { lastSyncTime: null, syncedBy: null }),
        catchError(this.errorHandler.handleError)
      );
  }

  public refreshPriceTrackingData(): Observable<{
    accepted: boolean;
    inProgress: boolean;
    lastSyncTime: string | null;
  }> {
    return this.http
      .post<{
        result?: {
          accepted: boolean;
          inProgress: boolean;
          lastSyncTime: string | null;
        };
      }>(`${this.PRODUCT_SEVICE_URL}/priceTracking/refreshData`, {})
      .pipe(
        map(
          (r) =>
            r?.result ?? {
              accepted: false,
              inProgress: false,
              lastSyncTime: null,
            }
        ),
        catchError(this.errorHandler.handleError)
      );
  }

  public getStoreCardData(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/storeVisitSummary`, {
        params: this.buildPriceTrackingDashboardQueryParams(filters),
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStoreGradeCoverage(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/storeGradeCoverage`, {
        params: this.buildPriceTrackingDashboardQueryParams(filters),
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStoreGradeCoverageStores(
    grade: string,
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    }
  ): Observable<any> {
    const params = this.buildPriceTrackingDashboardQueryParams(filters).set(
      'grade',
      grade
    );
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/storeGradeCoverage/stores`,
        { params }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayCoverageByStoreGrade(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/displayCoverageByStoreGrade`,
        { params: this.buildPriceTrackingDashboardQueryParams(filters) }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayCoverageByStoreGradeStores(
    grade: string,
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    }
  ): Observable<any> {
    const params = this.buildPriceTrackingDashboardQueryParams(filters).set(
      'grade',
      grade
    );
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/displayCoverageByStoreGrade/stores`,
        { params }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayCoverageTrend(
    dimension: 'state' | 'account' | 'category',
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    }
  ): Observable<any> {
    const params = this.buildPriceTrackingDashboardQueryParams(filters).set(
      'dimension',
      dimension
    );
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/displayCoverageTrend`, {
        params,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayCoverageTrendStores(
    dimension: 'state' | 'account' | 'category',
    dimensionId: number,
    month: number,
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    }
  ): Observable<any> {
    const params = this.buildPriceTrackingDashboardQueryParams(filters)
      .set('dimension', dimension)
      .set('dimensionId', String(dimensionId))
      .set('month', String(month));
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/displayCoverageTrend/stores`,
        { params }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayTracking(
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    },
    query: {
      pageIndex: number;
      pageSize: number;
      search?: string;
      display?: '' | 'yes' | 'no';
      sortCol?: string;
      sortDir?: 'asc' | 'desc';
    }
  ): Observable<any> {
    let params = this.buildPriceTrackingDashboardQueryParams(filters)
      .set('pageIndex', String(query.pageIndex))
      .set('pageSize', String(query.pageSize))
      .set('sortCol', query.sortCol || 'store')
      .set('sortDir', query.sortDir || 'asc');
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }
    if (query.display === 'yes' || query.display === 'no') {
      params = params.set('display', query.display);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/displayTracking`, {
        params,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayTrackingStoreDetail(
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    },
    storeId: number
  ): Observable<any> {
    const params = this.buildPriceTrackingDashboardQueryParams(filters).set(
      'storeId',
      String(storeId)
    );
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/displayTracking/storeDetail`,
        { params }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDisplayTracking(
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    },
    query: {
      search?: string;
      display?: '' | 'yes' | 'no';
      sortCol?: string;
      sortDir?: 'asc' | 'desc';
    }
  ): Observable<Blob> {
    let params = this.buildPriceTrackingDashboardQueryParams(filters)
      .set('sortCol', query.sortCol || 'store')
      .set('sortDir', query.sortDir || 'asc');
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }
    if (query.display === 'yes' || query.display === 'no') {
      params = params.set('display', query.display);
    }
    return this.http
      .get(`${this.PRODUCT_SEVICE_URL}/priceTracking/displayTracking/export`, {
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayTrackingByAccount(
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    },
    query: {
      pageIndex: number;
      pageSize: number;
      search?: string;
      sortCol?: string;
      sortDir?: 'asc' | 'desc';
    }
  ): Observable<any> {
    let params = this.buildPriceTrackingDashboardQueryParams(filters)
      .set('pageIndex', String(query.pageIndex))
      .set('pageSize', String(query.pageSize))
      .set('sortCol', query.sortCol || 'account')
      .set('sortDir', query.sortDir || 'asc');
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/displayTracking/accounts`, {
        params,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayTrackingAccountDetail(
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    },
    accountId: number
  ): Observable<any> {
    const params = this.buildPriceTrackingDashboardQueryParams(filters).set(
      'accountId',
      String(accountId)
    );
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/displayTracking/accountDetail`,
        { params }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDisplayTrackingByAccount(
    filters: {
      countryIds: number[];
      year?: number;
      monthIds?: number[];
      stateIds?: number[];
      cityIds?: number[];
      accountIds?: number[];
      storeIds?: number[];
      categoryIds?: number[];
      subcategoryIds?: number[];
      productIds?: number[];
      productTierIds?: number[];
      coreRangeIds?: number[];
    },
    query: {
      search?: string;
      sortCol?: string;
      sortDir?: 'asc' | 'desc';
    }
  ): Observable<Blob> {
    let params = this.buildPriceTrackingDashboardQueryParams(filters)
      .set('sortCol', query.sortCol || 'account')
      .set('sortDir', query.sortDir || 'asc');
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }
    return this.http
      .get(`${this.PRODUCT_SEVICE_URL}/priceTracking/displayTracking/accounts/export`, {
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getOverallStoreVisitSummary(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/overallStoreVisitSummary`, {
        params: this.buildPriceTrackingDashboardQueryParams(filters),
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getJpStoreVisitSummary(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/jpStoreVisitSummary`, {
        params: this.buildPriceTrackingDashboardQueryParams(filters),
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCoreRangeDisplaySummary(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/coreRangeDisplaySummary`, {
        params: this.buildPriceTrackingDashboardQueryParams(filters),
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getProductBalanceRecords(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/productBalanceRecords`, {
        params: this.buildPriceTrackingDashboardQueryParams(filters),
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportProductBalanceRecords(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
    coreRangeIds?: number[];
  }): Observable<Blob> {
    return this.http
      .post(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/productBalanceRecords/downloadExcelReport`,
        {},
        {
          params: this.buildPriceTrackingDashboardQueryParams(filters),
          responseType: 'blob'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTdsTdlUsersByCountry(countryIds: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if ((countryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/tdsTdlUsersByCountry`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTdsUsersByCountry(countryIds: number[], tdlUserIds?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if ((countryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    if ((tdlUserIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('tdlUserIds', tdlUserIds!.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/tdsUsersByCountry`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTdlUsersByCountry(countryIds: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if ((countryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/tdlUsersByCountry`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getNotVisitedStores(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams();
    if ((filters?.countryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('countryIds', filters.countryIds.join(','));
    }
    if (filters?.year != null && !Number.isNaN(Number(filters.year))) {
      queryParams = queryParams.set('year', String(filters.year));
    }
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.accountIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'accountIds',
        filters.accountIds?.join(',') ?? ''
      );
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/notVisitedStores`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportNotVisitedStores(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
  }): Observable<Blob> {
    let queryParams = new HttpParams();
    if ((filters?.countryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('countryIds', filters.countryIds.join(','));
    }
    if (filters?.year != null && !Number.isNaN(Number(filters.year))) {
      queryParams = queryParams.set('year', String(filters.year));
    }
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.accountIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'accountIds',
        filters.accountIds?.join(',') ?? ''
      );
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    return this.http
      .post(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/notVisitedStores/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getJpNotVisitedStores(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams();
    if ((filters?.countryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('countryIds', filters.countryIds.join(','));
    }
    if (filters?.year != null && !Number.isNaN(Number(filters.year))) {
      queryParams = queryParams.set('year', String(filters.year));
    }
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.accountIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'accountIds',
        filters.accountIds?.join(',') ?? ''
      );
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/priceTracking/jpNotVisitedStores`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportJpNotVisitedStores(filters: {
    countryIds: number[];
    year?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
  }): Observable<Blob> {
    let queryParams = new HttpParams();
    if ((filters?.countryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('countryIds', filters.countryIds.join(','));
    }
    if (filters?.year != null && !Number.isNaN(Number(filters.year))) {
      queryParams = queryParams.set('year', String(filters.year));
    }
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.accountIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'accountIds',
        filters.accountIds?.join(',') ?? ''
      );
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    return this.http
      .post(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/jpNotVisitedStores/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getComplianceTrendByState(filters: {
    countryIds: number[];
    year: number;
    view: 'month' | 'week';
    monthIds?: number[];
    weekIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('view', filters.view);
    if (
      filters.view === 'month' &&
      (filters?.monthIds?.length ?? 0) > 0
    ) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if (filters.view === 'week' && (filters?.weekIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('weekIds', filters.weekIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.accountIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'accountIds',
        filters.accountIds?.join(',') ?? ''
      );
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.productTierIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productTierIds',
        filters.productTierIds!.join(',')
      );
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/complianceTrendByState`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAccountComplianceTrend(filters: {
    countryIds: number[];
    year: number;
    accountId: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('accountId', String(filters.accountId));
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.productTierIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productTierIds',
        filters.productTierIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/accountComplianceTrend`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAccountPriceCompliance(filters: {
    countryIds: number[];
    year: number;
    accountId: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('accountId', String(filters.accountId));
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/accountPriceCompliance`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAccountComplianceTrendDrill(filters: {
    countryIds: number[];
    year: number;
    accountId: number;
    drillMonth: number;
    dimension: 'display' | 'price';
    outcome: 'ok' | 'fail' | 'all';
    grain?: 'product' | 'store';
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('accountId', String(filters.accountId))
      .set('month', String(filters.drillMonth))
      .set('dimension', filters.dimension)
      .set('outcome', filters.outcome)
      .set('grain', filters.grain ?? 'product');
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/accountComplianceTrendDrill`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportAccountComplianceTrendDrill(filters: {
    countryIds: number[];
    year: number;
    accountId: number;
    drillMonth: number;
    dimension: 'display' | 'price';
    outcome: 'ok' | 'fail' | 'all';
    grain?: 'product' | 'store';
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<Blob> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('accountId', String(filters.accountId))
      .set('month', String(filters.drillMonth))
      .set('dimension', filters.dimension)
      .set('outcome', filters.outcome)
      .set('grain', filters.grain ?? 'product');
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .post(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/accountComplianceTrendDrill/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadCoreRangeComplianceDrillExcelReport(filters: {
    countryIds: number[];
    year: number;
    accountId: number;
    drillBy: 'category' | 'state';
    categoryId?: number;
    stateId?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<Blob> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('accountId', String(filters.accountId))
      .set('drillBy', filters.drillBy);
    if (filters.drillBy === 'category' && filters.categoryId != null) {
      queryParams = queryParams.set(
        'drillCategoryId',
        String(filters.categoryId)
      );
    }
    if (filters.drillBy === 'state' && filters.stateId != null) {
      queryParams = queryParams.set('drillStateId', String(filters.stateId));
    }
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .post(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/coreRangeComplianceDrill/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCoreRangeComplianceByCategory(filters: {
    countryIds: number[];
    year: number;
    accountId: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('accountId', String(filters.accountId));
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.productTierIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productTierIds',
        filters.productTierIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/coreRangeComplianceByCategory`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCoreRangeComplianceDrill(filters: {
    countryIds: number[];
    year: number;
    accountId: number;
    drillBy: 'category' | 'state';
    categoryId?: number;
    stateId?: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('accountId', String(filters.accountId))
      .set('drillBy', filters.drillBy);
    if (filters.drillBy === 'category' && filters.categoryId != null) {
      queryParams = queryParams.set(
        'drillCategoryId',
        String(filters.categoryId)
      );
    }
    if (filters.drillBy === 'state' && filters.stateId != null) {
      queryParams = queryParams.set('drillStateId', String(filters.stateId));
    }
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/coreRangeComplianceDrill`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCoreRangeComplianceByState(filters: {
    countryIds: number[];
    year: number;
    accountId: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('accountId', String(filters.accountId));
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.productTierIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productTierIds',
        filters.productTierIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/coreRangeComplianceByState`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getComplianceByCategory(filters: {
    countryIds: number[];
    year: number;
    monthIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    productTierIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year));
    if ((filters?.monthIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.accountIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'accountIds',
        filters.accountIds?.join(',') ?? ''
      );
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.productTierIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productTierIds',
        filters.productTierIds!.join(',')
      );
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/complianceByCategory`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getComplianceBalanceRecords(filters: {
    countryIds: number[];
    year: number;
    view: 'month' | 'week';
    periodKey: number;
    areaGroupId: number;
    complianceType: 'display' | 'price';
    monthIds?: number[];
    weekIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('view', filters.view)
      .set('periodKey', String(filters.periodKey))
      .set('areaGroupId', String(filters.areaGroupId))
      .set('complianceType', filters.complianceType);
    if (
      filters.view === 'month' &&
      (filters?.monthIds?.length ?? 0) > 0
    ) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if (filters.view === 'week' && (filters?.weekIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('weekIds', filters.weekIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.accountIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'accountIds',
        filters.accountIds?.join(',') ?? ''
      );
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/complianceBalanceRecords`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportComplianceBalanceRecords(filters: {
    countryIds: number[];
    year: number;
    view: 'month' | 'week';
    periodKey: number;
    areaGroupId: number;
    complianceType: 'display' | 'price';
    monthIds?: number[];
    weekIds?: number[];
    stateIds?: number[];
    cityIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    categoryIds?: number[];
    subcategoryIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
  }): Observable<Blob> {
    let queryParams = new HttpParams()
      .set('countryIds', filters.countryIds.join(','))
      .set('year', String(filters.year))
      .set('view', filters.view)
      .set('periodKey', String(filters.periodKey))
      .set('areaGroupId', String(filters.areaGroupId))
      .set('complianceType', filters.complianceType);
    if (
      filters.view === 'month' &&
      (filters?.monthIds?.length ?? 0) > 0
    ) {
      queryParams = queryParams.set('monthIds', filters.monthIds!.join(','));
    }
    if (filters.view === 'week' && (filters?.weekIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('weekIds', filters.weekIds!.join(','));
    }
    if ((filters?.stateIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('stateIds', filters.stateIds?.join(',') ?? '');
    }
    if ((filters?.cityIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('cityIds', filters.cityIds?.join(',') ?? '');
    }
    if ((filters?.accountIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'accountIds',
        filters.accountIds?.join(',') ?? ''
      );
    }
    if ((filters?.storeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set('storeIds', filters.storeIds?.join(',') ?? '');
    }
    if ((filters?.categoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        filters.categoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.subcategoryIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'subcategoryIds',
        filters.subcategoryIds?.join(',') ?? ''
      );
    }
    if ((filters?.productIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'productIds',
        filters.productIds!.join(',')
      );
    }
    if ((filters?.coreRangeIds?.length ?? 0) > 0) {
      queryParams = queryParams.set(
        'coreRangeIds',
        filters.coreRangeIds!.join(',')
      );
    }
    return this.http
      .post(
        `${this.PRODUCT_SEVICE_URL}/priceTracking/complianceBalanceRecords/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCategoryByCountryFromView(countryId: number[]): Observable<any> {
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/category/getCategoryByCountryFromView`,
        { params: { countryId: countryId.join(',') } }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSubCategoryByCountryFromView(
    countryId: number[],
    categoryId?: number | number[]
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    const categoryIds = Array.isArray(categoryId)
      ? categoryId
      : categoryId != null
        ? [categoryId]
        : [];
    if (categoryIds.length > 0) {
      queryParams = queryParams.set('categoryId', categoryIds.join(','));
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/subCategory/getSubCategoryByCountryFromView`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //to fetch all fsbi
  public getFsBiAll(): Observable<any> {
    return this.http.get(`${this.PRODUCT_SEVICE_URL}/fs-bi/getAll`);
  }

  //to fetch all mda sda
  public getMdaSdaAll(): Observable<any> {
    return this.http.get(`${this.PRODUCT_SEVICE_URL}/mda-sda/getAll`);
  }

  public getAllProductLifecycleStatus(): Observable<any> {
    return this.http.get(`${this.PRODUCT_SEVICE_URL}/product-status/getAll`);
  }

  //to fetch all projectnames
  public getProjectNamesAll(countryIds?: number[]): Observable<any> {
    let params = new HttpParams();
    if (countryIds?.length) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http.get(`${this.PRODUCT_SEVICE_URL}/projectNames/getAll`, { params });
  }

  public static dedupeProjectNamesByName(
    items: Array<{ id: number; name: string }>,
  ): ProjectNameFilterOption[] {
    const byName = new Map<string, ProjectNameFilterOption>();

    for (const item of items ?? []) {
      if (!item?.name || !Number.isFinite(item.id)) {
        continue;
      }

      const key = item.name.trim().toLowerCase();
      const existing = byName.get(key);

      if (existing) {
        if (!existing.allIds.includes(item.id)) {
          existing.allIds.push(item.id);
        }
        continue;
      }

      byName.set(key, { id: item.id, name: item.name, allIds: [item.id] });
    }

    return Array.from(byName.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }

  public static expandProjectNameFilterIds(
    selectedIds: number[],
    options: ProjectNameFilterOption[],
  ): number[] {
    if (!selectedIds?.length) {
      return [];
    }

    const idSet = new Set<number>();

    for (const selectedId of selectedIds) {
      const option = options.find(
        (entry) =>
          entry.id === selectedId || entry.allIds.includes(selectedId),
      );

      if (option) {
        option.allIds.forEach((id) => idSet.add(id));
      } else {
        idSet.add(selectedId);
      }
    }

    return Array.from(idSet);
  }

  public getProjectNamesForFilter(
    countryIds?: number[],
  ): Observable<ProjectNameFilterOption[]> {
    return this.getProjectNamesAll(countryIds).pipe(
      map(
        (
          res:
            | { result?: Array<{ id: number; name: string }> }
            | Array<{ id: number; name: string }>,
        ) => {
          const raw = Array.isArray(res) ? res : res?.result ?? [];
          return ProductserviceService.dedupeProjectNamesByName(raw);
        },
      ),
    );
  }

  //to fetch all series
  public getSeriesAll(): Observable<any> {
    return this.http.get(`${this.PRODUCT_SEVICE_URL}/series/getAll`);
  }

  public tierGetAll(
    pageIndex: number = 1,
    pageSize: number = 100,
    searchFilter?: string
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/tier/getAll`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public tierSave(tierData: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/tier/save`, tierData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public tierUpdate(tierData: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/tier/update`, tierData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public tierDelete(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/tier/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //Get multi-brand mapping with pagination, search, and country filter
  public getMultiBrandMapping(
    pageIndex: number,
    pageSize: number,
    searchValue?: string,
    countryIds?: number[]
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    if (searchValue) {
      queryParams = queryParams.set('searchValue', searchValue);
    }

    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/brand/getMultiBrandMapping`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //Update multibrand (is_competitor) status
  public updateMultiBrandMapping(
    refId: number,
    isCompetitor: boolean
  ): Observable<any> {
    return this.http
      .put<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/updateMultiBrandMapping/${refId}`,
        { isCompetitor }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //Get promoter type brand mapping with pagination, search, and country filter
  public getPromoterTypeBrandMapping(
    pageIndex: number,
    pageSize: number,
    searchValue?: string,
    countryIds?: number[]
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    if (searchValue) {
      queryParams = queryParams.set('searchValue', searchValue);
    }

    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }

    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/getPromoterTypeBrandMapping`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //Get promoter type brand mapping by country ID - shows all brands with isPresent status
  public getPromoterTypeBrandMappingByCountryId(
    countryId: number
  ): Observable<any> {
    let queryParams = new HttpParams().set('countryId', countryId.toString());

    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/getPromoterTypeBrandMappingByCountryId`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public togglePromoterTypeBrandMapping(
    brandId: number,
    countryId: number,
    isBrand: boolean
  ): Observable<any> {
    const body = {
      brandId,
      countryId,
      isBrand,
    };

    return this.http
      .post<any>(
        `${this.PRODUCT_SEVICE_URL}/brand/togglePromoterTypeBrandMapping`,
        body
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To check the valid pnc code and model for product import
  // public checkValidPNCAndModel(existingPncCode: string[], existingModel: string[]): Observable<any> {
  //   let data = { existingPncCode, existingModel }
  //   return this.http
  //     .post<any>(`${this.PRODUCT_SEVICE_URL}/product/checkValidPNCAndModel`, data)
  //     .pipe(catchError(this.errorHandler.handleError));
  // }

  //To check the dropdown validation for product import
  // public dropdownDataCheck(data: any): Observable<any> {
  //   return this.http
  //     .post<any>(`${this.PRODUCT_SEVICE_URL}/product/dropdownDataCheck`, data)
  //     .pipe(catchError(this.errorHandler.handleError));
  // }

  //Get survey product mapping data
  public getSurveyProductMapping(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    countryIds?: number[];
    brandIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    yearIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        params.categoryIds.join(',')
      );
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set(
        'subCategoryIds',
        params.subCategoryIds.join(',')
      );
    }

    if (params.yearIds && params.yearIds.length > 0) {
      queryParams = queryParams.set('yearIds', params.yearIds.join(','));
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/survey-product-mapping/getAll`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createCategoryGroup(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.PRODUCT_SEVICE_URL}/incentive/createCategoryGroup`,
      data
    );
  }

  public createIncentiveRange(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.PRODUCT_SEVICE_URL}/incentive/createIncentiveRange`,
      data
    );
  }

  public getAllCategoryGroup(params: {
    pageIndex: number | null;
    pageSize: number | null;
    searchFilter?: string | null;
    countryIds?: number[];
    brandIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    year?: number;
    categoryGroupId?: number[],
    durationId?: number
  }): Observable<any> {


    let queryParams = new HttpParams();

    if (params.pageIndex !== null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex);
    }
    if (params.pageSize !== null) {
      queryParams = queryParams.set('pageSize', params.pageSize);
    }

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        params.categoryIds.join(',')
      );
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set(
        'subCategoryIds',
        params.subCategoryIds.join(',')
      );
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }


    if (params.durationId) {
      queryParams = queryParams.set('durationId', params.durationId);
    }


    if (params.categoryGroupId && params.categoryGroupId.length > 0) {
      queryParams = queryParams.set(
        'categoryGroupId',
        params.categoryGroupId.toString()
      );
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getAllCategoriesGroups`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllCategoryGroupsIncentive(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    countryIds?: number[];
    brandIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    year?: number;
    categoryGroupIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        params.categoryIds.join(',')
      );
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set(
        'subCategoryIds',
        params.subCategoryIds.join(',')
      );
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    if (params.categoryGroupIds && params.categoryGroupIds.length > 0) {
      queryParams = queryParams.set('categorygroupId', params.categoryGroupIds.toLocaleString());
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/getAllCategoryGroupsIncentive`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteCategoryGroup(id: number): Observable<any> {
    return this.http
      .delete<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/categoryGroup/delete/${id}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteIncentiveSlab(id: number): Observable<any> {
    return this.http
      .delete<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/incentiveSlabs/delete/${id}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getPmiDateRanges(countryIds?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryIds?.length) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getPmiDateRanges`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public checkPmiDateRangeOverlap(params: {
    countryIds?: number[];
    fromDate: string;
    toDate: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('fromDate', params.fromDate)
      .set('toDate', params.toDate);
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/checkPmiDateRangeOverlap`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deletePmiDateRange(params: {
    countryIds?: number[];
    fromDate: string;
    toDate: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('fromDate', params.fromDate)
      .set('toDate', params.toDate);
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/incentive/deletePmiDateRange`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllTierLevels(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    countryIds?: number[];
    brandIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    year?: number;
    months?: number;
    periodType?: string;
    fromDate?: string;
    toDate?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        params.categoryIds.join(',')
      );
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set(
        'subCategoryIds',
        params.subCategoryIds.join(',')
      );
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    if (params.months && params.months) {
      queryParams = queryParams.set('months', params.months.toString());
    }
    if (params.periodType) {
      queryParams = queryParams.set('periodType', params.periodType);
    }
    if (params.fromDate) {
      queryParams = queryParams.set('fromDate', params.fromDate);
    }
    if (params.toDate) {
      queryParams = queryParams.set('toDate', params.toDate);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getAllTierLevels`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadTiersLevelTemplate(params: {
    pageIndex?: number | null;
    pageSize?: number | null;
    searchFilter?: string;
    countryIds?: number[];
    brandIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    month?: number;
    year?: number;
    periodType?: string;
    fromDate?: string;
    toDate?: string;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        params.categoryIds.join(',')
      );
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set(
        'subCategoryIds',
        params.subCategoryIds.join(',')
      );
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    if (params.month) {
      queryParams = queryParams.set('months', params.month.toString());
    }
    if (params.periodType) {
      queryParams = queryParams.set('periodType', params.periodType);
    }
    if (params.fromDate) {
      queryParams = queryParams.set('fromDate', params.fromDate);
    }
    if (params.toDate) {
      queryParams = queryParams.set('toDate', params.toDate);
    }

    return this.http
      .post<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/tierLevels/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importIncentiveTierLevels(
    data: Record<string, any>
  ): Observable<any[]> {
    return this.http
      .post<any[]>(
        `${this.PRODUCT_SEVICE_URL}/incentive/updateIncentiveTierLevels`,
        data
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public clearOtherTierIncentives(data: {
    periodType: 'MONTHLY' | 'CUSTOM';
    country: string;
    month?: number | null;
    year?: number | null;
    fromDate?: string | null;
    toDate?: string | null;
    keepProductIds: number[];
  }): Observable<any> {
    return this.http
      .post<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/clearOtherTierIncentives`,
        data
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTierRawIncentiveReport(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    countryIds?: number[];
    brandIds?: number[];
    userIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    promoterIds?: number[];
    year?: number | null;
    month?: number[] | null;
    periodType?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.userIds && params.userIds.length > 0) {
      queryParams = queryParams.set('userIds', params.userIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        params.categoryIds.join(',')
      );
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set(
        'subCategoryIds',
        params.subCategoryIds.join(',')
      );
    }

    if (params.promoterIds && params.promoterIds.length > 0) {
      queryParams = queryParams.set(
        'promoterIds',
        params.promoterIds.join(',')
      );
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    if (params.month && params.month.length > 0) {
      queryParams = queryParams.set('month', params.month.join(','));
    }
    if (params.periodType) {
      queryParams = queryParams.set('periodType', params.periodType);
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/getTierRawIncentiveReport`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportTierRawIncentiveReport(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    countryIds?: number[];
    brandIds?: number[];
    userIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    year?: number | null;
    month?: number[] | null;
    periodType?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.userIds && params.userIds.length > 0) {
      queryParams = queryParams.set('userIds', params.userIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        params.categoryIds.join(',')
      );
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set(
        'subCategoryIds',
        params.subCategoryIds.join(',')
      );
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    if (params.month && params.month.length > 0) {
      queryParams = queryParams.set('month', params.month.join(','));
    }
    if (params.periodType) {
      queryParams = queryParams.set('periodType', params.periodType);
    }
    return this.http
      .post<Blob>(
        `${this.PRODUCT_SEVICE_URL}/incentive/incentiveReport/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get category group target data for table
  public getCategoryGroupTargetData(params: {
    pageIndex: number;
    pageSize: number;
    countryId?: number[];
    month?: number[];
    year?: number;
    searchFilter?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryId.join(','));
    }

    if (params.month && params.month.length > 0) {
      queryParams = queryParams.set('month', params.month.join(','));
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    if (params.searchFilter?.trim()) {
      queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
    }

    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/getAllCategoryGroupsTargets`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get category group target data for table
  public getExistingCategoryGroupTargetData(params: {
    pageIndex: number;
    pageSize: number;
    countryId?: number[];
    month?: number[];
    year?: number;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryId.join(','));
    }

    if (params.month && params.month.length > 0) {
      queryParams = queryParams.set('month', params.month.join(','));
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/getExistingCategoryGroupTargetData`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get category group target data for table
  public saveCategoryGroupTargetData(body: any): Observable<any> {
    return this.http
      .post<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/saveCategoryTargets`,
        body
      )
      .pipe(catchError(this.errorHandler.handleError));
  }




  public saveIncentiveSchemeAchievements(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.PRODUCT_SEVICE_URL}/incentive/saveIncentiveSchemeAchievements`,
      data
    );
  }

  public getAllIncentiveSchemeAchievements(params: {
    pageIndex: number | null;
    pageSize: number | null;
    searchFilter?: string | null;
    countryIds?: number[];
    year?: number;
    month?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.pageIndex !== null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex);
    }
    if (params.pageSize !== null) {
      queryParams = queryParams.set('pageSize', params.pageSize);
    }

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year);
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/getAllIncentiveSchemeAchievements`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteIncentiveSchemeAchievement(id: number): Observable<any> {
    return this.http
      .delete<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/incentiveSchemeAchievementRange/delete/${id}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getPmiTiers(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getPmiTiers`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getPmiIncentiveSummary(params: {
    countryIds?: number[];
    month?: number;
    year?: number;
    searchFilter?: string;
    pageIndex?: number;
    pageSize?: number;
    channelIds?: number[];
    accountIds?: number[];
    incentiveSchemeIds?: number[];
    tierIds?: number[];
    periodType?: string;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month != null) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.searchFilter?.trim()) {
      queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
    }
    if (params.pageIndex != null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize != null) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    if (params.channelIds?.length) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds?.length) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.incentiveSchemeIds?.length) {
      queryParams = queryParams.set('incentiveSchemeIds', params.incentiveSchemeIds.join(','));
    }
    if (params.tierIds?.length) {
      queryParams = queryParams.set('tierIds', params.tierIds.join(','));
    }
    if (params.periodType) {
      queryParams = queryParams.set('periodType', params.periodType);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getPmiIncentiveSummary`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getPmiIncentiveDetails(params: {
    countryIds?: number[];
    month?: number;
    year?: number;
    searchFilter?: string;
    promoterIds?: number[];
    pageIndex?: number;
    pageSize?: number;
    channelIds?: number[];
    accountIds?: number[];
    incentiveSchemeIds?: number[];
    tierIds?: number[];
    periodType?: string;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month != null) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.searchFilter?.trim()) {
      queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
    }
    if (params.promoterIds?.length) {
      queryParams = queryParams.set('promoterIds', params.promoterIds.join(','));
    }
    if (params.pageIndex != null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize != null) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    if (params.channelIds?.length) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds?.length) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.incentiveSchemeIds?.length) {
      queryParams = queryParams.set('incentiveSchemeIds', params.incentiveSchemeIds.join(','));
    }
    if (params.tierIds?.length) {
      queryParams = queryParams.set('tierIds', params.tierIds.join(','));
    }
    if (params.periodType) {
      queryParams = queryParams.set('periodType', params.periodType);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getPmiIncentiveDetails`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getMonthlyIncentiveAddOn(params: {
    countryIds?: number[];
    month?: number;
    year?: number;
    searchFilter?: string;
    pageIndex?: number;
    pageSize?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month != null) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.searchFilter?.trim()) {
      queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
    }
    if (params.pageIndex != null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize != null) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getMonthlyIncentiveAddOn`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getMonthlyIncentiveSpecialPeriods(params: {
    countryIds?: number[];
    month?: number;
    year?: number;
    searchFilter?: string;
    pageIndex?: number;
    pageSize?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month != null) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.searchFilter?.trim()) {
      queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
    }
    if (params.pageIndex != null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize != null) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getMonthlyIncentiveSpecialPeriods`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCategoryGroupTargetAchievement(params: {
    countryIds?: number[];
    month?: number[];
    year?: number;
    pageIndex?: number;
    pageSize?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month?.length) {
      queryParams = queryParams.set('month', params.month.join(','));
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.pageIndex != null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize != null) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getCategoryGroupTargetAchievement`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCategoryGroupTargetAchievementWeb(params: {
    countryIds?: number[];
    month?: number[];
    year?: number;
    pageIndex?: number;
    pageSize?: number;
    typeId?: number;
    includeResults?: boolean;
    includeOverall?: boolean;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month?.length) {
      queryParams = queryParams.set('month', params.month.join(','));
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.pageIndex != null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize != null) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    if (params.typeId != null) {
      queryParams = queryParams.set('typeId', params.typeId.toString());
    }
    if (params.includeResults != null) {
      queryParams = queryParams.set('includeResults', String(params.includeResults));
    }
    if (params.includeOverall != null) {
      queryParams = queryParams.set('includeOverall', String(params.includeOverall));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getCategoryGroupTargetAchievementWeb`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getEgyptIncentiveReportDetailsWeb(params: {
    countryIds?: number[];
    month?: number[];
    year?: number;
    pageIndex?: number;
    pageSize?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month?.length) {
      queryParams = queryParams.set('month', params.month.join(','));
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.pageIndex != null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize != null) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/getEgyptIncentiveReportDetailsWeb`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Get all models for dashboard with filters
  public getAllModelsDashboard(countryId?: number[], subCategoryId?: number[], categoryId?: number[], searchFilter?: string, projectId?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (subCategoryId) {
      queryParams = queryParams.set('subCategoryId', subCategoryId.join(','));
    }
    if (categoryId) {
      queryParams = queryParams.set('categoryId', categoryId.join(','));
    }
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (projectId && Array.isArray(projectId)) {
      queryParams = queryParams.set('projectId', projectId.join(','));
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/getAllModelsDashboard`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }



  ///bundel Offer

  public createBundleOffer(formData: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/create`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getBundleOffers(params: {
    pageIndex: number | null;
    pageSize: number | null;
    searchFilter?: string | null;
    countryIds?: number[];
    status?: number;
    startDate?: string | null;
    endDate?: string | null;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.pageIndex !== null) {
      queryParams = queryParams.set('pageIndex', params.pageIndex);
    }
    if (params.pageSize !== null) {
      queryParams = queryParams.set('pageSize', params.pageSize);
    }

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.status) {
      queryParams = queryParams.set('status', params.status.toString());
    }

    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/getAllBundleOffers`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateBundleOfferStatus(id: number, status: boolean): Observable<any> {
    return this.http
      .put<any>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/updateStatus`, { id, status })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteBundleOffer(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getBundleOfferById(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/getBundleOfferById/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateBundleOffer(formData: any): Observable<any> {
    return this.http
      .put<any>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/update`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportBundleOffersExcel(countryId: number): Observable<Blob> {
    return this.http
      .get<Blob>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/exportExcel`, {
        params: { countryId: countryId.toString() },
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadBundleOfferTemplate(countryId?: number): Observable<Blob> {
    let params = new HttpParams();
    if (countryId != null && countryId > 0) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .get<Blob>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/downloadExcelTemplate`, {
        params,
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public precheckBundleOfferImport(countryId: number, rows: any[]): Observable<{
    rowResults: { rowIndex: number; excelRowNum: number; action: 'Add' | 'Edit' | 'Delete'; valid: boolean; errors: string[] }[];
    summary: { totalActionRows: number; validCount: number; invalidCount: number };
  }> {
    return this.http
      .post<{
        rowResults: { rowIndex: number; excelRowNum: number; action: 'Add' | 'Edit' | 'Delete'; valid: boolean; errors: string[] }[];
        summary: { totalActionRows: number; validCount: number; invalidCount: number };
      }>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/precheckImport`, {
        countryId,
        rows,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importBundleOffers(countryId: number, rows: any[]): Observable<{ success: number; errors: string[] }> {
    return this.http
      .post<{ success: number; errors: string[] }>(`${this.PRODUCT_SEVICE_URL}/bundleOffer/importExcel`, {
        countryId,
        rows,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //// SAP Sync services
  public getSapMissingCountByCountry(regionIds?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (regionIds && regionIds.length > 0) {
      queryParams = queryParams.set('regionId', regionIds.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/missing-count-by-country`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSapUnsyncedProducts(pageIndex: number, pageSize: number, countryId?: number, categoryId?: string[], subCategoryId?: string[], brandId?: string[], searchValue?: string): Observable<any> {



    let queryParams = new HttpParams();
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }
    if (pageIndex !== undefined && pageIndex !== null) {
      queryParams = queryParams.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      queryParams = queryParams.set('pageSize', pageSize.toString());
    }

    if (categoryId && categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', categoryId.toString());
    }


    if (subCategoryId && subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', subCategoryId.toString());
    }


    if (brandId && brandId.length > 0) {
      queryParams = queryParams.set('brandId', brandId.toString());
    }
    if (searchValue) {
      queryParams = queryParams.set('searchValue', searchValue);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/unsynced-products`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public downloadTemplateSynSapProducts(countryId?: number, categoryId?: string[], subCategoryId?: string[], brandId?: string[]): Observable<Blob> {
    let queryParams = new HttpParams();

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }

    if (categoryId && categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', categoryId.toString());
    }


    if (subCategoryId && subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', subCategoryId.toString());
    }


    if (brandId && brandId.length > 0) {
      queryParams = queryParams.set('brandId', brandId.toString());
    }
    return this.http
      .get<Blob>(`${this.PRODUCT_SEVICE_URL}/sap/downloadExcelTemplate`, {
        params: queryParams,
        responseType: 'blob' as 'json'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }
  public syncProductsFromSap(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/sap/sync-products`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public syncProductsFromSapimports(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/sap/sync-products-imports`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public validateSyncProductsFromSapimports(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/sap/validate-sync-imports`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }
  public getAllProductlineByCountry(countryId?: number): Observable<any> {

    let queryParams = new HttpParams();

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/product-line`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllBrandsByCountry(countryId?: number): Observable<any> {

    let queryParams = new HttpParams();

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/brands`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //to fetch all fsbi
  public getFsBiAllByCountry(countryId?: number): Observable<any> {
    let queryParams = new HttpParams();

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http.get(`${this.PRODUCT_SEVICE_URL}/sap/fsbi`, { params: queryParams });
  }

  //to fetch all mdasda
  public getMdSdaAllByCountry(countryId?: number): Observable<any> {
    let queryParams = new HttpParams();

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http.get(`${this.PRODUCT_SEVICE_URL}/sap/mdasda`, { params: queryParams });
  }

  public getProductSegmentByCountryId(countryId?: number): Observable<any> {
    let queryParams = new HttpParams();

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/segments`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllCategoriesByCountry(params: {
    countryId?: number
    productLineId?: number
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }
    if (params.productLineId) {
      queryParams = queryParams.set('productLineId', params.productLineId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/categories`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllStoreTargets(params: {
    pageIndex: number;
    pageSize: number;
    countryIds?: number[];
    year?: number;
    month?: number;
    storeIds?: number[];
    searchFilter?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year);
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/store-target/getAll`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllSubCategoriesByCategory(
    countryId: number[],
    categoryId?: number
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (categoryId && categoryId !== 0) {
      queryParams = queryParams.set('categoryId', categoryId);
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/sap/sub-categories`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getStoresByCountry(countryIds: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/store-target/getStoresByCountry`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createOrUpdateStoreTarget(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/store-target/createOrUpdate`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importStoreTargets(data: any[]): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/store-target/import`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteStoreTarget(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/store-target/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getIncentiveAddOnList(params: {
    pageIndex: number;
    pageSize: number;
    countryIds?: number[];
    year?: number;
    month?: number;
    searchFilter?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month != null) {
      queryParams = queryParams.set('month', params.month);
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year);
    }
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/incentiveAddOn/list`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createOrUpdateIncentiveAddOn(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/incentive/incentiveAddOn`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteIncentiveAddOn(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/incentive/incentiveAddOn/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getIncentiveSpecialDatesList(params: {
    pageIndex: number;
    pageSize: number;
    countryIds?: number[];
    year?: number;
    month?: number;
    searchFilter?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month != null) {
      queryParams = queryParams.set('month', params.month);
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year);
    }
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/incentive/incentiveSpecialDates/list`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createOrUpdateIncentiveSpecialDates(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/incentive/incentiveSpecialDates`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteIncentiveSpecialDates(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/incentive/incentiveSpecialDates/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }  // =====================================
  // SAP Fetch Data APIs (Distinct Values)
  // =====================================

  public getAllSAPProductlineByCountry(countryId?: number): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/sap-product-line`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllSAPBrandsByCountry(countryId?: number): Observable<any> {

    let queryParams = new HttpParams();

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/sap-brands`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllSAPCategoriesByCountry(params: {
    countryId?: number
    productLine?: string
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }
    if (params.productLine) {
      queryParams = queryParams.set('productLine', params.productLine);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/sap-categories`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllSAPSubCategoriesByCategory(
    countryId: number[],
    categoryId?: number
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (categoryId) {
      queryParams = queryParams.set('categoryId', categoryId);
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/sap/sap-sub-categories`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllCategoryGroupsIncentiveDetails(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    countryIds?: number[];
    brandIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    year?: number;
    categoryGroupIds?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set(
        'categoryIds',
        params.categoryIds.join(',')
      );
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set(
        'subCategoryIds',
        params.subCategoryIds.join(',')
      );
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    if (params.categoryGroupIds && params.categoryGroupIds.length > 0) {
      queryParams = queryParams.set('categorygroupId', params.categoryGroupIds.toString());
    }
    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/getAllCategoryGroupsIncentiveDetails`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadPromoterCategoryGroupTemplate(
    params: {
      countryId: number,
      year: number,
      month: number
    }
  ): Observable<any> {
    let queryParams = new HttpParams()

    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }


    if (params.month) {
      queryParams = queryParams.set('month', params.month.toString());
    }

    return this.http
      .post<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importPromoterCategoryGroupTarget(data: Record<string, any>): Observable<any[]> {
    return this.http
      .post<any[]>(
        `${this.PRODUCT_SEVICE_URL}/incentive/saveCategoryTargets`,
        data
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public existingCategoryGroupSchemeCheck(params: {

    countryId?: number;
    fromDate?: string,
    toDate?: string,
    typeId?: number
  }): Observable<any> {
    let queryParams = new HttpParams()

    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }
    if (params.fromDate) {
      queryParams = queryParams.set('startDate', params.fromDate);
    }

    if (params.toDate) {
      queryParams = queryParams.set('endDate', params.toDate);
    }

    if (params.typeId) {
      queryParams = queryParams.set('typeId', params.typeId);
    }



    return this.http
      .get<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/existingCategoryGroupSchemeCheck`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public deleteSummaryCategoryGroupIncentive(id: number): Observable<any> {
    return this.http
      .delete<any>(
        `${this.PRODUCT_SEVICE_URL}/incentive/deleteSummaryCategoryGroupIncentive/delete/${id}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllSources(params: {

    countryId?: number;

  }): Observable<any[]> {
    let queryParams = new HttpParams();
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }
    return this.http
      .get<any[]>(`${this.PRODUCT_SEVICE_URL}/product/getAllSources`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStarProductTiers(countryId?: number): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/product/getStarProductTiers`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }



  public getAllChannelTypes(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/channel-type/getAll`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // ==========================================
  // SAP Product Mapping APIs
  // ==========================================

  // Product Line Mapping
  public getProductlineMappings(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/product-line`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createOrUpdateProductlineMapping(data: { id?: number; sapProductlineId: string; intexProductlineId: number }): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/product-line`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteProductlineMapping(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/product-line/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Category Mapping
  public getCategoryMappings(productlineMapId?: number): Observable<any> {
    let queryParams = new HttpParams();
    if (productlineMapId) {
      queryParams = queryParams.set('productlineMapId', productlineMapId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/category`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createOrUpdateCategoryMapping(data: { id?: number; productlineMapId: number; sapCategoryId: string; intexCategoryId: number }): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/category`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteCategoryMapping(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/category/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Subcategory Mapping
  public getSubcategoryMappings(categoryMapId?: number): Observable<any> {
    let queryParams = new HttpParams();
    if (categoryMapId) {
      queryParams = queryParams.set('categoryMapId', categoryMapId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/subcategory`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createOrUpdateSubcategoryMapping(data: { id?: number; categoryMapId: number; sapSubcategoryId: string; intexSubcategoryId: number }): Observable<any> {
    return this.http
      .post<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/subcategory`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteSubcategoryMapping(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/subcategory/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Unmapped Items
  public getUnmappedItems(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/mapping/unmapped`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Global Intelux Fetching Methods
  public getGlobalProductLines(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/global-product-lines`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getGlobalCategories(productLineId?: string): Observable<any> {
    let params = new HttpParams();
    if (productLineId) {
      params = params.set('productLineId', productLineId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/global-categories`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getGlobalSubCategories(categoryId: string): Observable<any> {
    let params = new HttpParams();
    if (categoryId) {
      params = params.set('categoryId', categoryId);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/global-sub-categories`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Global SAP Fetching Methods
  public getGlobalSAPProductLines(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/global-sap-product-lines`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getGlobalSAPCategories(productLine?: string): Observable<any> {
    let params = new HttpParams();
    if (productLine) {
      params = params.set('productLine', productLine);
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/global-sap-categories`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getGlobalSAPSubCategories(categoryNames?: string[]): Observable<any> {
    let params = new HttpParams();
    if (categoryNames && categoryNames.length > 0) {
      params = params.set('categoryId', categoryNames.join(','));
    }
    return this.http
      .get<any>(`${this.PRODUCT_SEVICE_URL}/sap/global-sap-sub-categories`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

}
